import { customElement, FASTElement, observable, html } from '@microsoft/fast-element';
import { VppaFormTemplate as template } from './vppa-form.template';
import { VppaFormStyles as styles } from './vppa-form.styles';
import { CASCADE_ENUM, EnumTranslate, IEnumService } from '../../../../services/enum.service';
import { logger, Message, SocketSubscription } from '@genesislcap/foundation-comms';
import { ICommonService, IProjectService, IVppaService } from '../../../../services';
import { IHubZoneMatrix, IVppa, IVppaInsert, Project } from '../../../../types';
import { updateArray } from '../../../../utils/dataserver';
import { getAbbreviationForCreditProfile } from '../../../../utils/credit-profiles';
import { NumberField, TextField } from '@microsoft/fast-foundation';
import { Select } from '@genesislcap/foundation-ui';
import {
  clearErrorUtil,
  clearStandingMessageUtil,
  CONTRACT_TYPE,
  displayErrorUtil,
  displayStandingMessageUtil,
  PROFILE_RIGHTS,
  PROJECT_OPERATIONAL_STATUS,
  VPPA_RFQ_STATUS,
  PRODUCT_TYPE,
} from '../../../../utils';
import { formatDateForInput, formatDateLong, roundNumber } from '../../../../utils/formatting';
import { ICompanyService } from '../../../../services/company.service';
import { SegmentedControl } from '@genesislcap/foundation-zero';
import { IUserService } from '../../../../services/user.service';

const name = 'vppa-form';

@customElement({
  name,
  template,
  styles,
})
export class VppaForm extends FASTElement {
  @IUserService userService: IUserService;
  // Services used
  @IEnumService enumService: IEnumService;
  @IProjectService projectService: IProjectService;
  @observable productTypeElement: Select;
  @ICommonService commonService: ICommonService;
  @IVppaService vppaService: IVppaService;
  @ICompanyService companyService: ICompanyService;
  // HTML objects reference
  @observable projectElement: Select;
  @observable legalEntityElement: Select;
  @observable vppaStatusElement: Select;
  @observable contractTypeElement: Select;
  @observable projectIsoElement: TextField;
  @observable vppaRefElement: TextField;
  @observable settlementPointElement: Select;
  @observable settlementIndexElement: Select;
  @observable fixedPriceElement: NumberField;
  @observable tenorStartDateElement: TextField;
  @observable tenorMonthElement: NumberField;
  @observable offeredCapacityElement: TextField;
  @observable buyersFractionElement: TextField;
  @observable negativePriceMethodElement: Select;
  @observable applicableRECProgramElement: Select;
  @observable applicableTrackingSystem: Select;
  @observable recDeliveryMethodologyElement: Select;
  @observable performanceGuaranteeMethodElement: Select;
  @observable creditProfileElement: Select;
  @observable sellerPostCodCollateralElement: TextField;
  @observable techTypeElement: SegmentedControl;
  @observable floorQuantityElement: NumberField;

  // ENUMs list
  @observable vppaStatuses: Array<EnumTranslate>;
  @observable contractTypes: Array<EnumTranslate> = [];
  @observable negativePricingMethod: Array<EnumTranslate>;
  @observable applicableRECProgram: Array<EnumTranslate>;
  @observable applicableTrackingSystemEnum: Array<EnumTranslate>;
  @observable upsideSharing: Array<EnumTranslate>;
  @observable collar: Array<EnumTranslate>;
  @observable buyerUpsideAllocation: Array<EnumTranslate>;
  @observable recDeliveryMethodology: Array<EnumTranslate>;
  @observable performanceGuaranteeMethod: Array<EnumTranslate>;
  @observable productTypes: Array<EnumTranslate>;
  @observable creditProfiles: Array<EnumTranslate>;

  // Project List
  @observable projectList: Array<Project> = [];
  private currentProject: Project;

  // HubZone matrix List related
  private _hubZoneMatrix: Array<IHubZoneMatrix> = [];
  private _filteredHubZoneMatrix: Array<IHubZoneMatrix> = [];
  @observable _settlementPoint: Array<string> = [];
  @observable _settlementIndex: Array<string> = [];
  @observable vppaLegalEntityList: Array<string> = [];

  // Values inserted on fields
  @observable projectValue: string;
  @observable legalEntityValue: string;
  @observable vppaStatusValue: string;
  @observable contractTypeValue: string;
  @observable settlementPointValue: string;
  @observable settlementIndexValue: string;
  @observable fixedPriceValue: string = '';
  @observable tenorStartDateValue: any;
  @observable tenorMonthValue: string = '';
  @observable offeredCapacityValue: string = '';
  @observable buyersFractionValue: string = '';
  @observable negativePriceMethodValue: string;
  @observable applicableRECProgramValue: string;
  @observable applicableTrackingSystemValue: string;
  @observable recDeliveryMethodologyValue: string;
  @observable performanceGuaranteeMethodValue: string;
  @observable projectIso: string;
  @observable vppaRefValue: string;
  @observable creditProfileValue: string;
  @observable sellerPostCodCollateralValue: string = '';
  @observable techTypeValue: 'WIND' | 'SOLAR' = 'WIND';
  @observable productTypeValue: string;
  @observable isProjectOperational: boolean = false;
  @observable floorQuantityValue: string = '';
  @observable megawattAc: string = '';

  public deafultPostCodCollateralValue = 100000;

  // Control
  @observable isEditModal: boolean = false;
  @observable vppaId: string;
  @observable isKeyMetricsWillBeUpdated: boolean = false;
  private hubZoneMatrixIdValue: string;
  private _requiredFormFields = [];
  private projectSubscription: SocketSubscription;
  private existingVppa: IVppa;
  private isTenorDateValid: boolean = false;
  private isBuyerFractionValid: boolean = false;
  private isTenorMonthsValid: boolean = false;
  private isCapacityValid: boolean = false;
  private isContractTypeValid: boolean = false;
  @observable isCreditWorthy: boolean;
  @observable isBusinessHours: boolean;
  // Only false if trying to auto execute outside of business hours
  @observable isAbleToExecute: boolean = true;
  @observable sellerPostCodCollateralPlaceholder: string = '';

  @observable contractTypesDisplayed: Array<EnumTranslate>;
  @observable productTypesDisplayed: Array<EnumTranslate>;

  public autoExecString: string = 'MINI_POST_BUILD_VPPA_AUTO_EXECUTED';

  async connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.projectSubscription?.unsubscribe();
  }

  public async loadData(vppaId: string, projectId: string, isEditModal: boolean) {
    this.vppaId = vppaId;
    this.isEditModal = isEditModal;
    await this.loadEnums();
    await this.loadHubZoneMatrix();
    await this.loadBusinessHourFlag();
    await this.loadCreditWorthiness();
    if (!this.isCreditWorthy) {
      this.sellerPostCodCollateralValue = this.deafultPostCodCollateralValue
        ? this.deafultPostCodCollateralValue.toString()
        : '';
    }
    this.loadRequiredFields();
    this.filteredContractTypes();
    this.streamProjects(projectId);
  }

  async loadVppaData() {
    const response = await this.vppaService.getVppa(this.vppaId);
    if (response.REPLY) {
      this.isKeyMetricsWillBeUpdated = false;
      this.existingVppa = response.REPLY[0];
      this.productTypeValue = this.existingVppa.PRODUCT_TYPE;
      this.projectValue = this.existingVppa.PROJECT_ID;
      this.projectSelected();
      this.projectLoad();
      this.vppaStatusValue = this.existingVppa.VPPA_STATUS;
      this.contractTypeValue = this.existingVppa.CONTRACT_TYPE;
      if (this.contractTypeValue === this.autoExecString) {
        this.isAbleToExecute = this.isBusinessHours;
      }
      this.hubZoneMatrixIdValue = this.existingVppa.HUB_ZONE_MATRIX_ID;
      this.fixedPriceValue = this.existingVppa.FIXED_PRICE
        ? this.existingVppa.FIXED_PRICE.toString()
        : '';

      if (this.existingVppa.TENOR_START_DATE) {
        this.tenorStartDateValue = formatDateForInput(this.existingVppa.TENOR_START_DATE as number);
        this.checkTenorValidity();
      }

      if (this.existingVppa.TENOR_MONTHS) {
        this.tenorMonthValue = this.existingVppa.TENOR_MONTHS
          ? this.existingVppa.TENOR_MONTHS.toString()
          : '';
        setTimeout(() => {
          this.checkTenorMonthsValidity();
        }, 100);
      }

      if (this.existingVppa.OFFERED_CAPACITY) {
        this.offeredCapacityValue = this.existingVppa.OFFERED_CAPACITY
          ? this.existingVppa.OFFERED_CAPACITY.toString()
          : '';
        setTimeout(() => {
          this.checkOfferedCapacityValidity();
        }, 100);
      }

      if (this.existingVppa.BUYERS_FRACTION) {
        this.buyersFractionValue = this.existingVppa.BUYERS_FRACTION
          ? this.existingVppa.BUYERS_FRACTION.toString()
          : '';
        setTimeout(() => {
          this.validateBuyerFraction();
        }, 100);
      }

      this.buyersFractionValue = this.existingVppa.BUYERS_FRACTION
        ? this.existingVppa.BUYERS_FRACTION.toString()
        : '';
      this.negativePriceMethodValue = this.existingVppa.NEGATIVE_PRICING_METHOD;
      this.applicableRECProgramValue = this.existingVppa.APPLICABLE_REC_PROGRAM;
      this.applicableTrackingSystemValue = this.existingVppa.APPLICABLE_TRACKING_SYSTEM;
      this.recDeliveryMethodologyValue = this.existingVppa.REC_DELIVERY_METHODOLOGY;
      this.performanceGuaranteeMethodValue = this.existingVppa.PERFORMANCE_GUARANTEE_METHOD;
      this.vppaRefValue = this.existingVppa.VPPA_REFERENCE;
      this.creditProfileValue = this.existingVppa.VPPA_CREDIT_PROFILE;
      this.sellerPostCodCollateralPlaceholder = getAbbreviationForCreditProfile(
        this.existingVppa.VPPA_CREDIT_PROFILE,
      );
      this.sellerPostCodCollateralValue = this.existingVppa.SELLER_POST_COD_COLLATERAL
        ? this.existingVppa.SELLER_POST_COD_COLLATERAL.toString()
        : '';
      this.floorQuantityValue = this.existingVppa.FLOOR_QUANTITY
        ? this.existingVppa.FLOOR_QUANTITY.toString()
        : '';
      this.entityLegalNameLoad();
      if (this.contractTypeValue) {
        this.isContractTypeValid = true;
      }
      this.loadRequiredFields();
      setTimeout(() => {
        this.productTypeElement.value = this.productTypeValue;
      }, 100);
    }
  }

  private cleanRecOnlyValues() {
    if (this.productTypeValue !== PRODUCT_TYPE.RECS) {
      this.floorQuantityValue = null;
    }
  }

  public projectSelected() {
    this.projectIso = '';
    this.techTypeValue = 'WIND';
    this.settlementPointValue = '';
    this.settlementIndexValue = '';
    this.hubZoneMatrixIdValue = '';
    this._settlementPoint = [];
    this._settlementIndex = [];
    this._filteredHubZoneMatrix = [];
    this.vppaLegalEntityList = [];
    this.vppaLegalEntityList.push(this.companyService.getCurrentCompany().NAME);

    if (this.projectValue) {
      this.currentProject = this.projectList.find(
        (element) => element.PROJECT_ID === this.projectValue,
      );
      this.vppaLegalEntityList.push(this.currentProject.NAME);
      this.projectIso = this.currentProject.ISO;
      this.megawattAc = this.currentProject.MEGAWATT_AC
        ? this.currentProject.MEGAWATT_AC.toString()
        : '';
      this.techTypeValue = this.currentProject.TECHNOLOGY;
      if (this.currentProject.PROJECT_OPERATIONAL_STATUS === PROJECT_OPERATIONAL_STATUS.NEW_BUILD) {
        this.tenorStartDateValue = formatDateForInput(this.currentProject.TARGET_COD as number);
      } else {
        this.tenorStartDateValue = '';
      }

      this._filteredHubZoneMatrix = this._hubZoneMatrix.filter(
        (item) => item.ISO === this.projectIso,
      );

      if (
        this.currentProject.PROJECT_OPERATIONAL_STATUS === PROJECT_OPERATIONAL_STATUS.OPERATIONAL
      ) {
        this.isProjectOperational = true;
        this.filteredContractTypes();
        this.filteredProductTypes();
      } else {
        this.isProjectOperational = false;
        this.filteredContractTypes();
        this.filteredProductTypes();
      }

      this._settlementPoint = [
        ...new Set(this._filteredHubZoneMatrix.map((item) => item.SETTLEMENT_POINT)),
      ];
    }
    this.validateTenorStartDateValue();
  }

  private entityLegalNameLoad() {
    this.vppaLegalEntityList = [];
    this.vppaLegalEntityList.push(this.companyService.getCurrentCompany().NAME);
    const projectAux = this.projectList.find((element) => element.PROJECT_ID === this.projectValue);
    this.vppaLegalEntityList.push(projectAux.NAME);
    setTimeout(() => {
      this.legalEntityValue = this.existingVppa.VPPA_LEGAL_ENTITY;
    }, 100);
  }

  public projectLoad() {
    if (this.projectValue && this.projectList.length > 0) {
      this.projectIso = this.projectList.find(
        (element) => element.PROJECT_ID === this.projectValue,
      ).ISO;

      this._filteredHubZoneMatrix = this._hubZoneMatrix.filter(
        (item) => item.ISO === this.projectIso,
      );

      this._settlementPoint = [
        ...new Set(this._filteredHubZoneMatrix.map((item) => item.SETTLEMENT_POINT)),
      ];

      this._filteredHubZoneMatrix = this._hubZoneMatrix.filter(
        (item) => item.SETTLEMENT_POINT === this.existingVppa.SETTLEMENT_POINT,
      );
      this._settlementIndex = [
        ...new Set(this._filteredHubZoneMatrix.map((item) => item.SETTLEMENT_INDEX)),
      ];

      setTimeout(() => {
        this.settlementPointValue = this.existingVppa.SETTLEMENT_POINT;
        this.settlementIndexValue = this.existingVppa.SETTLEMENT_INDEX;
        setTimeout(() => {
          this.validateTenorStartDateValue();
          this.checkFormCompletionStatus();
        }, 100);
      }, 100);
    }
  }

  public contractTypeValueChanged() {
    if (this.offeredCapacityValue) {
      this.checkOfferedCapacityValidity();
    }
    if (this.tenorStartDateValue) {
      this.checkTenorValidity();
    }
    if (this.tenorMonthValue) {
      this.checkTenorMonthsValidity();
    }

    if (this.contractTypeValue) {
      this.isContractTypeValid = true;
      clearErrorUtil(this.contractTypeElement);
    } else if (
      this.productTypeValue &&
      this.isContractTypeValid === false &&
      !this.contractTypeValue
    ) {
      displayErrorUtil(this.contractTypeElement);
    }

    if (this.contractTypeValue === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED) {
      this.buyersFractionElement.removeEventListener('blur', () => this.roundBuyersFractional());
      this.isAbleToExecute = this.isBusinessHours;
    } else {
      this.isAbleToExecute = true;
    }

    this.loadRequiredFields();
  }

  public settlementPointSelected() {
    this._settlementIndex = [];
    this.settlementIndexValue = '';
    this.hubZoneMatrixIdValue = '';
    this.getFilteredHubZoneMatrix();
  }

  public applicableRECProgramSelected() {
    this.applicableTrackingSystemValue = undefined;
    this.recDeliveryMethodologyValue = undefined;
    clearErrorUtil(this.recDeliveryMethodologyElement);
    clearErrorUtil(this.applicableTrackingSystem);
  }

  public creditProfileSelected() {
    let creditProfileAbbreviation = getAbbreviationForCreditProfile(this.creditProfileValue);
    this.sellerPostCodCollateralPlaceholder = creditProfileAbbreviation;
    if (this.creditProfileValue == 'LETTER_OF_CREDIT') {
      // Check if the _requiredFormFields contains sellerPostCodCollateralElement
      const hasSellerPostCodCollateralElement = this._requiredFormFields.some(
        (field) => field === this.sellerPostCodCollateralElement,
      );

      if (!hasSellerPostCodCollateralElement) {
        // Add sellerPostCodCollateralElement if not present
        this._requiredFormFields.push(this.sellerPostCodCollateralElement);
      }
      this.sellerPostCodCollateralValue = this.deafultPostCodCollateralValue.toString();
      this.sellerPostCodCollateralElement.value = this.sellerPostCodCollateralValue;
    } else {
      this.sellerPostCodCollateralValue = '';

      // Try to remove sellerPostCodCollateralElement if we have it
      this._requiredFormFields = this._requiredFormFields.filter(
        (field) => field !== this.sellerPostCodCollateralElement,
      );
    }
    this.checkFormCompletionStatus();
  }

  private getFilteredHubZoneMatrix() {
    if (this.settlementPointValue) {
      this._filteredHubZoneMatrix = this._hubZoneMatrix.filter(
        (item) => item.SETTLEMENT_POINT === this.settlementPointValue,
      );
      this._settlementIndex = [
        ...new Set(this._filteredHubZoneMatrix.map((item) => item.SETTLEMENT_INDEX)),
      ];
    }
  }

  public settlementIndexSelected() {
    const selectedHubZoneMatrix = this._filteredHubZoneMatrix.find(
      (item) => item.SETTLEMENT_INDEX === this.settlementIndexValue,
    );

    if (selectedHubZoneMatrix) {
      this.hubZoneMatrixIdValue = selectedHubZoneMatrix.HUB_ZONE_MATRIX_ID;
    }
  }

  public validateTenorStartDateValue() {
    if (this.projectValue && this.tenorStartDateValue) {
      const project = this.projectList.find((element) => element.PROJECT_ID === this.projectValue);
      const todaysDate = new Date();
      todaysDate.setUTCHours(0, 0, 0, 0);
      const todaysDateTime = todaysDate.getTime();
      const startDate = new Date(this.tenorStartDateValue);

      const startDateTime = startDate.getTime();

      let validCod: any;

      if (project.PROJECT_OPERATIONAL_STATUS === PROJECT_OPERATIONAL_STATUS.NEW_BUILD) {
        validCod = project.TARGET_COD;
      } else {
        validCod = project.COD;
      }

      if (todaysDateTime >= validCod && startDateTime < todaysDateTime) {
        clearStandingMessageUtil(this.tenorStartDateElement);
        displayErrorUtil(
          this.tenorStartDateElement,
          'Tenor Start Date must be greater than or equal to ' + formatDateLong(todaysDateTime),
        );
        this.isTenorDateValid = false;
        return;
      }

      if (startDateTime >= validCod) {
        clearErrorUtil(this.tenorStartDateElement);
        displayStandingMessageUtil(
          this.tenorStartDateElement,
          'Key Metrics will start on the first full month of the contract.',
        );
        this.isTenorDateValid = true;
      } else {
        clearStandingMessageUtil(this.tenorStartDateElement);
        displayErrorUtil(
          this.tenorStartDateElement,
          'Tenor Start Date must be greater than or equal to ' + formatDateLong(validCod),
        );
        this.isTenorDateValid = false;
      }
      if (this.contractTypeValue === this.autoExecString && !this.checkDate(startDate)) {
        clearStandingMessageUtil(this.tenorStartDateElement);
        displayErrorUtil(this.tenorStartDateElement, 'Must be less than 12 months forward');
        this.isTenorDateValid = false;
      }
      if (this.contractTypeValue === this.autoExecString && this.tenorMonthValue) {
        this.checkTenorMonthsValidity();
      }

      return;
    }
    clearErrorUtil(this.tenorStartDateElement);
    displayStandingMessageUtil(
      this.tenorStartDateElement,
      'Key Metrics will start on the first full month of the contract.',
    );
  }

  public clearForm() {
    this.cleanClassValues();

    this.projectSubscription?.unsubscribe();
    this.projectList = [];

    this._hubZoneMatrix = [];
    this._filteredHubZoneMatrix = [];
    this._settlementPoint = [];
    this._settlementIndex = [];
    this.vppaLegalEntityList = [];

    this.isEditModal = false;
    this.vppaId = null;
    this.clearFieldValidity();
    clearErrorUtil(this.recDeliveryMethodologyElement);
    clearErrorUtil(this.applicableTrackingSystem);
  }

  public reloadVppaData() {
    if (this.isEditModal) {
      this.loadVppaData();
      this.cleanClassValues();
    }
  }

  public cleanClassValues() {
    this.projectValue = null;
    this.productTypeValue = null;
    this.vppaStatusValue = VPPA_RFQ_STATUS.ACTIVE;
    this.contractTypeValue = null;
    this.settlementPointValue = null;
    this.settlementIndexValue = null;
    this.fixedPriceValue = null;
    this.tenorStartDateValue = null;
    this.tenorMonthValue = null;
    this.offeredCapacityValue = null;
    this.buyersFractionValue = null;
    this.negativePriceMethodValue = undefined;
    this.recDeliveryMethodologyValue = undefined;
    this.performanceGuaranteeMethodValue = undefined;
    this.creditProfileValue = null;
    this.sellerPostCodCollateralValue = null;
    this.sellerPostCodCollateralPlaceholder = null;
    this.projectIso = null;
    this.vppaRefValue = null;
    this.legalEntityValue = null;
    this.floorQuantityValue = null;
    this.applicableRECProgramValue = undefined;
    this.applicableTrackingSystemValue = undefined;
    this.recDeliveryMethodologyValue = undefined;
  }

  private async loadCreditWorthiness() {
    this.isCreditWorthy = await this.companyService.isCreditWorthy();
  }

  private async loadBusinessHourFlag() {
    await this.commonService.isItIsBuyingHours().then((response) => {
      if (response.REPLY) {
        this.isBusinessHours = response.REPLY[0].IS_IT_IS_BUYING_HOURS;
      }
    });
  }

  private async loadEnums() {
    await this.enumService.getFilteredEnumValues(CASCADE_ENUM.CONTRACT_TYPE).then((response) => {
      if (response.REPLY) {
        this.contractTypes = response.REPLY;
      }
    });

    await this.enumService.getFilteredEnumValues(CASCADE_ENUM.PRODUCT_TYPE).then((response) => {
      if (response.REPLY) {
        this.productTypes = response.REPLY;
      }
    });

    await this.enumService.getEnumValues(CASCADE_ENUM.NEGATIVE_PRICING_METHOD).then((response) => {
      if (response.REPLY) {
        this.negativePricingMethod = response.REPLY;
      }
    });

    await this.enumService.getEnumValues(CASCADE_ENUM.APPLICABLE_REC_PROGRAM).then((response) => {
      if (response.REPLY) {
        this.applicableRECProgram = response.REPLY;
      }
    });

    await this.enumService
      .getEnumValues(CASCADE_ENUM.APPLICABLE_TRACKING_SYSTEM)
      .then((response) => {
        if (response.REPLY) {
          this.applicableTrackingSystemEnum = response.REPLY;
        }
      });

    await this.enumService.getEnumValues(CASCADE_ENUM.REC_DELIVERY_METHODOLOGY).then((response) => {
      if (response.REPLY) {
        this.recDeliveryMethodology = response.REPLY;
      }
    });

    await this.enumService
      .getEnumValues(CASCADE_ENUM.PERFORMANCE_GUARANTEE_METHOD)
      .then((response) => {
        if (response.REPLY) {
          this.performanceGuaranteeMethod = response.REPLY;
        }
      });

    await this.enumService.getEnumValues(CASCADE_ENUM.VPPA_STATUS).then((response) => {
      if (response.REPLY) {
        this.vppaStatuses = response.REPLY;
      }
    });

    await this.enumService.getEnumValues(CASCADE_ENUM.VPPA_CREDIT_PROFILE).then((response) => {
      if (response.REPLY) {
        const companyCreditProfile = this.companyService.getCurrentCompany().CREDIT_PROFILE;

        // Define the allowed names based on the company's credit profile
        let allowedNames: string[] = [];

        if (companyCreditProfile === 'INVESTMENT_GRADE') {
          allowedNames = [
            'INVESTMENT_GRADE_ENTITY',
            'PARENTAL_GUARANTEE_FROM_INVESTMENT_ENTITY',
            'LETTER_OF_CREDIT',
            'PROJECT_LIEN',
            'OTHER',
          ];
        } else if (companyCreditProfile === 'NON_INVESTMENT_GRADE') {
          allowedNames = [
            'PARENTAL_GUARANTEE_FROM_NON_INVESTMENT_ENTITY',
            'LETTER_OF_CREDIT',
            'PROJECT_LIEN',
            'OTHER',
          ];
        } else {
          allowedNames = [];
        }

        // Filter the credit profiles based on allowed names
        this.creditProfiles = response.REPLY.filter((item) =>
          allowedNames.includes(item.ENUM_VALUE),
        );
      }
    });
  }

  private streamProjects(projectId: string) {
    let resultList = [];
    this.projectSubscription = this.projectService.getAllProjects('').subscribe({
      next: (streamMessage: Message) => {
        resultList = streamMessage.ROW ?? null;
        if (resultList) {
          this.projectList = updateArray(resultList, this.projectList).sort(
            (a, b) => a.NAME - b.NAME,
          );
          if (this.vppaId) {
            setTimeout(() => {
              this.loadVppaData();
            }, 200);
          } else {
            setTimeout(() => {
              this.projectValue = projectId;
              this.projectSelected();
            }, 200);
          }
        } else {
          this.projectList = [];
          const resultError = streamMessage.DETAILS?.ERROR;
          logger.error(resultError);
        }
      },
      error: (error: any) => {
        this.projectList = [];
        logger.error(error);
      },
    });
  }

  private async loadHubZoneMatrix() {
    await this.commonService.getHubZoneMatrix().then((response) => {
      if (response.REPLY) {
        this._hubZoneMatrix = response.REPLY;
      }
    });
  }

  public loadRequiredFields() {
    this._requiredFormFields = [
      this.projectElement,
      this.productTypeElement,
      this.contractTypeElement,
      this.projectIsoElement,
      this.legalEntityElement,
      this.fixedPriceElement,
      this.tenorStartDateElement,
      this.tenorMonthElement,
      this.offeredCapacityElement,
      this.buyersFractionElement,
    ];
    if (this.contractTypeValue !== this.autoExecString) {
      this._requiredFormFields.push(this.performanceGuaranteeMethodElement);
      this._requiredFormFields.push(this.applicableRECProgramElement);
      this._requiredFormFields.push(this.creditProfileElement);
      if (this.creditProfileValue == 'LETTER_OF_CREDIT') {
        this._requiredFormFields.push(this.sellerPostCodCollateralElement);
      }
      if (this.productTypeValue != PRODUCT_TYPE.RECS) {
        this._requiredFormFields.push(this.negativePriceMethodElement);
      }
    }
    if (this.productTypeValue != PRODUCT_TYPE.RECS) {
      this._requiredFormFields.push(this.settlementPointElement, this.settlementIndexElement);
    }
    if (this.applicableRECProgramValue != 'NONE' && this.contractTypeValue != this.autoExecString) {
      this._requiredFormFields.push(
        this.applicableTrackingSystem,
        this.recDeliveryMethodologyElement,
      );
    }

    this._requiredFormFields.forEach((field) => {
      if (field === this.tenorStartDateElement) {
        field.addEventListener('change', () => this.checkTenorValidity());
        field.addEventListener('change', () => this.keyMetricsWouldBeUpdated());
        return;
      }

      if (field === this.buyersFractionElement) {
        field.addEventListener('blur', () => this.calculateOfferedCapacity());
        field.addEventListener('change', () => this.keyMetricsWouldBeUpdated());
        return;
      }

      if (field === this.offeredCapacityElement) {
        field.addEventListener('change', () => this.checkOfferedCapacityValidity());
        return;
      }

      if (field === this.fixedPriceElement) {
        field.addEventListener('blur', () => this.checkFieldValidity(field));
        field.addEventListener('change', () => this.keyMetricsWouldBeUpdated());
        return;
      }

      if (field === this.tenorMonthElement) {
        field.addEventListener('blur', () => this.checkTenorMonthsValidity());
        field.addEventListener('change', () => this.keyMetricsWouldBeUpdated());
        return;
      }
      if (field === this.productTypeElement) {
        field.addEventListener('change', () => this.checkProductTypeValidity());
      }

      if (field instanceof Select) {
        field.addEventListener('change', () => this.checkFieldValidity(field));
      }

      if (field == this.applicableRECProgramElement) {
        field.addEventListener('change', () => this.checkApplicableRecValidity());
      }

      if (field == this.creditProfileElement) {
        field.addEventListener('change', () => this.creditProfileSelected());
      }

      field.addEventListener('blur', () => this.checkFieldValidity(field));
    });

    this.checkFormCompletionStatus();
  }

  private checkTenorValidity() {
    this.validateTenorStartDateValue();
    this.checkFormCompletionStatus();
  }

  public checkTenorMonthsValidity() {
    this.isTenorMonthsValid = true;
    clearErrorUtil(this.tenorMonthElement);
    if (!this.tenorMonthValue) {
      displayErrorUtil(this.tenorMonthElement, 'Cannot be empty');
      this.isTenorMonthsValid = false;
    }
    const startDate = new Date(this.tenorStartDateValue);
    if (
      this.contractTypeValue === this.autoExecString &&
      !this.checkDate(startDate, Number(this.tenorMonthValue))
    ) {
      displayErrorUtil(
        this.tenorMonthElement,
        'Tenor Start Date + months must be less than 12 months forward',
      );
      this.isTenorMonthsValid = false;
    }

    this.checkFormCompletionStatus();
  }

  private checkOfferedCapacityValidity() {
    this.isCapacityValid = true;
    clearErrorUtil(this.offeredCapacityElement);

    if (this.contractTypeValue !== this.autoExecString) {
      this.checkFieldValidity(this.offeredCapacityElement);
      return;
    }

    if (!this.offeredCapacityValue) {
      displayErrorUtil(this.offeredCapacityElement);
    } else if (Number(this.offeredCapacityValue) > 10 || Number(this.offeredCapacityValue) < 1) {
      this.isCapacityValid = false;
      displayErrorUtil(this.offeredCapacityElement, 'Must be between 1MW and 10MW');
    }
    this.checkFormCompletionStatus();
  }

  private checkFieldValidity(elm: TextField | Select | NumberField) {
    if (!elm.value) {
      displayErrorUtil(elm);
    } else {
      clearErrorUtil(elm);
    }
    this.checkFormCompletionStatus();
  }

  private clearFieldValidity() {
    this._requiredFormFields.forEach((field) => {
      clearErrorUtil(field);
    });
  }

  private checkFormCompletionStatus() {
    const isFormValid =
      this.isTenorDateValid &&
      this.isTenorMonthsValid &&
      this.isBuyerFractionValid &&
      this.isCapacityValid &&
      this.isContractTypeValid &&
      this._requiredFormFields.every((elm) => elm.value);
    this.$emit('formChanged', isFormValid);
  }

  public getFormData(): IVppaInsert {
    this.handleRECValues();
    const formData: IVppaInsert = {
      PROJECT_ID: this.projectValue,
      VPPA_STATUS: this.vppaStatusValue,
      CONTRACT_TYPE: this.contractTypeValue,
      OFFERED_CAPACITY: Number(this.offeredCapacityValue),
      FIXED_PRICE: Number(this.fixedPriceValue),
      TENOR_START_DATE: this.tenorStartDateValue,
      TENOR_MONTHS: Number(this.tenorMonthValue),
      SETTLEMENT_POINT: this.settlementPointValue,
      SETTLEMENT_INDEX: this.settlementIndexValue,
      HUB_ZONE_MATRIX_ID: this.hubZoneMatrixIdValue,
      REC_DELIVERY_METHODOLOGY: this.recDeliveryMethodologyValue
        ? this.recDeliveryMethodologyValue
        : 'NONE',
      PERFORMANCE_GUARANTEE_METHOD: this.performanceGuaranteeMethodValue,
      BUYERS_FRACTION: Number(this.buyersFractionValue),
      NEGATIVE_PRICING_METHOD:
        this.productTypeValue != PRODUCT_TYPE.RECS
          ? this.negativePriceMethodValue
          : 'NOT_APPLICABLE',
      APPLICABLE_REC_PROGRAM: this.applicableRECProgramValue
        ? this.applicableRECProgramValue
        : 'NONE',
      APPLICABLE_TRACKING_SYSTEM: this.applicableTrackingSystemValue
        ? this.applicableTrackingSystemValue
        : 'NOT_APPLICABLE',
      VPPA_LEGAL_ENTITY: this.legalEntityValue,
      VPPA_REFERENCE: this.vppaRefValue,
      VPPA_CREDIT_PROFILE: this.creditProfileValue,
      SELLER_POST_COD_COLLATERAL: Number(this.sellerPostCodCollateralValue),
      PRODUCT_TYPE: this.productTypeValue,
      FLOOR_QUANTITY: Number(this.floorQuantityValue),
      CASCADE_COMPANY_ID: this.companyService.getCurrentCompany().CASCADE_COMPANY_ID,
    };

    return formData;
  }

  public getFullFormData(): IVppa {
    this.handleRECValues();
    this.existingVppa.OFFERED_CAPACITY = Number(this.offeredCapacityValue);
    this.existingVppa.FIXED_PRICE = Number(this.fixedPriceValue);
    this.existingVppa.TENOR_START_DATE = this.tenorStartDateValue;
    this.existingVppa.TENOR_MONTHS = Number(this.tenorMonthValue);
    this.existingVppa.SETTLEMENT_INDEX = this.settlementIndexValue;
    this.existingVppa.HUB_ZONE_MATRIX_ID = this.hubZoneMatrixIdValue;
    this.existingVppa.REC_DELIVERY_METHODOLOGY = this.recDeliveryMethodologyValue
      ? this.recDeliveryMethodologyValue
      : 'NONE';
    this.existingVppa.PERFORMANCE_GUARANTEE_METHOD = this.performanceGuaranteeMethodValue;
    this.existingVppa.BUYERS_FRACTION = Number(this.buyersFractionValue);
    this.existingVppa.NEGATIVE_PRICING_METHOD =
      this.productTypeValue != PRODUCT_TYPE.RECS ? this.negativePriceMethodValue : 'NOT_APPLICABLE';
    this.existingVppa.APPLICABLE_REC_PROGRAM = this.applicableRECProgramValue
      ? this.applicableRECProgramValue
      : 'NONE';
    this.existingVppa.APPLICABLE_TRACKING_SYSTEM = this.applicableTrackingSystemValue
      ? this.applicableTrackingSystemValue
      : 'NOT_APPLICABLE';
    this.existingVppa.VPPA_REFERENCE = this.vppaRefValue;
    this.existingVppa.VPPA_CREDIT_PROFILE = this.creditProfileValue;
    this.existingVppa.SELLER_POST_COD_COLLATERAL = Number(this.sellerPostCodCollateralValue);
    this.existingVppa.VPPA_STATUS = this.vppaStatusValue;
    this.existingVppa.PRODUCT_TYPE = this.productTypeValue;
    this.existingVppa.FLOOR_QUANTITY = Number(this.floorQuantityValue);

    return this.existingVppa;
  }

  private async handleRECValues() {
    if (this.productTypeValue === PRODUCT_TYPE.RECS) {
      this.settlementPointValue = this.currentProject.SETTLEMENT_POINT;
      this.settlementIndexValue = 'DAY_AHEAD';
      this.settlementIndexSelected();
    }
  }

  private checkDate(dateTime: Date, numMonths?: number): boolean {
    const currentDate = new Date();
    const futureDate = new Date();
    futureDate.setFullYear(
      dateTime.getFullYear(),
      dateTime.getMonth() + (numMonths || 0),
      dateTime.getDate(),
    );
    const maxDate = new Date();
    maxDate.setFullYear(
      currentDate.getFullYear() + 1,
      currentDate.getMonth(),
      currentDate.getDate(),
    );
    return futureDate.getTime() < maxDate.getTime();
  }

  public checkProductTypeValidity() {
    this.isContractTypeValid = true;
    this.cleanRecOnlyValues();
    if (this.productTypeValue) {
      setTimeout(() => {
        if (this.productTypeValue === PRODUCT_TYPE.RECS) {
          this.contractTypeValue = CONTRACT_TYPE.REC;
          this.isContractTypeValid = true;
          clearErrorUtil(this.contractTypeElement);
          return;
        } else if (this.productTypeValue && this.contractTypeValue) {
          this.contractTypeValue = null;
          clearErrorUtil(this.productTypeElement);
          this.isContractTypeValid = false;
          this.contractTypeValueChanged();
        }
        this.checkFormCompletionStatus();
      }, 10);
    }
    this.filteredContractTypes();
  }

  public checkApplicableRecValidity() {
    if (this.applicableRECProgramValue == 'NONE') {
      this._requiredFormFields = this._requiredFormFields.filter(
        (x) => x !== this.recDeliveryMethodologyElement && x !== this.applicableTrackingSystem,
      );
    } else {
      this._requiredFormFields.push(
        this.recDeliveryMethodologyElement,
        this.applicableTrackingSystem,
      );
    }
    this.checkFormCompletionStatus();
  }

  private filteredByOperationalStatus() {
    const newBuildFilter: String[] = [CONTRACT_TYPE.NEW_BUILD_PPA, CONTRACT_TYPE.NEW_BUILD_VPPA];
    if (this.isProjectOperational) {
      this.contractTypesDisplayed = this.contractTypesDisplayed.filter(
        (x) => !newBuildFilter.includes(x.ENUM_VALUE),
      );
    } else {
      this.contractTypesDisplayed = this.contractTypesDisplayed.filter((x) =>
        newBuildFilter.includes(x.ENUM_VALUE),
      );
    }
  }

  public filteredContractTypes() {
    const virtualPpaFilter: String[] = [
      CONTRACT_TYPE.NEW_BUILD_VPPA,
      CONTRACT_TYPE.POST_BUILD_VPPA,
    ];
    const physicalPpaFilter: String[] = [CONTRACT_TYPE.NEW_BUILD_PPA, CONTRACT_TYPE.POST_BUILD_PPA];
    const recFilter: String[] = [CONTRACT_TYPE.REC];
    if (this.contractTypes) {
      switch (this.productTypeValue) {
        case PRODUCT_TYPE.VIRTUAL_PPA: {
          this.contractTypesDisplayed = this.contractTypes.filter(
            (x) =>
              virtualPpaFilter.includes(x.ENUM_VALUE) ||
              (x.ENUM_VALUE === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED &&
                this.companyService.canAutoExecute() &&
                this.userService.getFilteredPermissions().includes(PROFILE_RIGHTS.AUTO_EXECUTE) &&
                this.isProjectOperational),
          );
          this.filteredByOperationalStatus();
          break;
        }

        case PRODUCT_TYPE.PHYSICAL_PPA: {
          this.contractTypesDisplayed = this.contractTypes.filter((x) =>
            physicalPpaFilter.includes(x.ENUM_VALUE),
          );
          this.filteredByOperationalStatus();
          break;
        }
        case PRODUCT_TYPE.RECS: {
          this.contractTypesDisplayed = this.contractTypes.filter((x) =>
            recFilter.includes(x.ENUM_VALUE),
          );
          break;
        }
        default: {
          this.contractTypesDisplayed = this.contractTypes;
          break;
        }
      }
    }
  }
  public filteredProductTypes() {
    this.productTypesDisplayed = this.productTypes;
  }

  public validateBuyerFraction() {
    this.isBuyerFractionValid = true;
    clearErrorUtil(this.buyersFractionElement);
    if (!this.buyersFractionValue) {
      displayErrorUtil(this.buyersFractionElement);
    } else if (
      Number(this.buyersFractionValue) <= 0.0 ||
      Number(this.buyersFractionValue) > 100.0
    ) {
      this.isBuyerFractionValid = false;
      displayErrorUtil(
        this.buyersFractionElement,
        "Buyer's Fraction % must be between 0 and 100 percent",
      );
    }
    this.keyMetricsWouldBeUpdated();
    this.checkFormCompletionStatus();
  }

  private calculateOfferedCapacity() {
    if (!this.buyersFractionValue) {
      displayErrorUtil(this.buyersFractionElement, "Buyer's fraction cannot be empty");
      return;
    }
    this.offeredCapacityValue = roundNumber(
      Number(this.megawattAc) * (Number(this.buyersFractionValue) / 100),
      3,
      10,
      'UP',
    ).toString();
    setTimeout(() => {
      this.checkOfferedCapacityValidity();
    }, 100);
  }

  private keyMetricsWouldBeUpdated() {
    if (this.isEditModal) {
      this.isKeyMetricsWillBeUpdated =
        this.fixedPriceValue !=
          (this.existingVppa.FIXED_PRICE ? this.existingVppa.FIXED_PRICE.toString() : '') ||
        this.tenorStartDateValue !=
          formatDateForInput(this.existingVppa.TENOR_START_DATE as number) ||
        this.tenorMonthValue !=
          (this.existingVppa.TENOR_MONTHS ? this.existingVppa.TENOR_MONTHS.toString() : '') ||
        this.buyersFractionValue !=
          (this.existingVppa.BUYERS_FRACTION ? this.existingVppa.BUYERS_FRACTION.toString() : '');
    }
  }

  public roundBuyersFractional() {
    this.buyersFractionValue = roundNumber(this.buyersFractionElement.value)
      ? roundNumber(this.buyersFractionElement.value).toString()
      : '';
  }

  public getBuyersCapacityLabel() {
    return this.isProjectOperational
      ? html`
          Buyer's Capacity (MW
          <sub>AC</sub>
          )
        `
      : html`
          Expected Buyer's Capacity (MW
          <sub>AC</sub>
          )
        `;
  }
}
